import { useState, useEffect } from 'react';

interface CookieConsentData {
  desktop_text: string;
  mobile_text: string;
  accept_button_text: string;
  reject_button_text: string;
  privacy_policy_url: string;
  privacy_policy_label: string;
}

const DEFAULTS: CookieConsentData = {
  desktop_text: 'Este website utiliza cookies para melhorar a sua experiência de navegação. Ao continuar a navegar, concorda com a nossa utilização de cookies. Consulte a nossa {privacy_link} para mais informações.',
  mobile_text: 'Este website utiliza cookies para melhorar a sua experiência. Consulte a nossa {privacy_link}.',
  accept_button_text: 'Aceitar Cookies',
  reject_button_text: 'Rejeitar',
  privacy_policy_url: 'https://financor.pt/politica-de-privacidade/',
  privacy_policy_label: 'Política de Privacidade'
};

export default function CookieConsentBar() {
  const [isVisible, setIsVisible] = useState(false);
  const [data, setData] = useState<CookieConsentData>(DEFAULTS);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      fetchData();
      const timer = setTimeout(() => setIsVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch('https://mulata.pt/bolacha-mulata/api/cookie_consent.php');
      const result = await response.json();
      if (result) {
        setData({
          desktop_text: result.desktop_text || DEFAULTS.desktop_text,
          mobile_text: result.mobile_text || DEFAULTS.mobile_text,
          accept_button_text: result.accept_button_text || DEFAULTS.accept_button_text,
          reject_button_text: result.reject_button_text || DEFAULTS.reject_button_text,
          privacy_policy_url: result.privacy_policy_url || DEFAULTS.privacy_policy_url,
          privacy_policy_label: result.privacy_policy_label || DEFAULTS.privacy_policy_label
        });
      }
    } catch (err) {
      console.error('Error loading cookie consent data:', err);
    }
  };

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setIsVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem('cookieConsent', 'rejected');
    setIsVisible(false);
  };

  const renderText = (text: string) => {
    if (!text) return null;

    const parts = text.split('{privacy_link}');
    return parts.map((part, i) => (
      <span key={i}>
        {part}
        {i < parts.length - 1 && (
          <a
            href={data.privacy_policy_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#f0c070',
              textDecoration: 'underline',
              textUnderlineOffset: '2px'
            }}
          >
            {data.privacy_policy_label}
          </a>
        )}
      </span>
    ));
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Desktop */}
      <div
        className="cookie-consent-desktop fixed bottom-0 left-0 right-0 z-[9998]"
        style={{
          animation: 'slideUp 0.5s ease-out forwards'
        }}
      >
        <div
          style={{
            background: 'linear-gradient(135deg, #3a1a0a 0%, #562310 50%, #6B3410 100%)',
            borderTop: '3px solid #8B4513',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)'
          }}
        >
          <div
            className="flex items-center justify-between"
            style={{
              maxWidth: '1400px',
              margin: '0 auto',
              padding: '1.2vw 3vw'
            }}
          >
            {/* Cookie icon + Text */}
            <div className="flex items-center" style={{ gap: '1.2vw', flex: 1 }}>
              <span style={{ fontSize: '2vw', flexShrink: 0 }}>🍪</span>
              <p
                className="prompt-regular-font"
                style={{
                  color: '#ffffff',
                  fontSize: '0.85vw',
                  lineHeight: '1.5',
                  margin: 0
                }}
              >
                {renderText(data.desktop_text)}
              </p>
            </div>

            {/* Buttons */}
            <div className="flex items-center" style={{ gap: '0.8vw', flexShrink: 0, marginLeft: '2vw' }}>
              <button
                onClick={handleReject}
                className="prompt-medium-font"
                style={{
                  color: '#d4a574',
                  background: 'transparent',
                  border: '1.5px solid #d4a574',
                  borderRadius: '8px',
                  padding: '0.5vw 1.5vw',
                  fontSize: '0.78vw',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(212, 165, 116, 0.15)';
                  e.currentTarget.style.borderColor = '#f0c070';
                  e.currentTarget.style.color = '#f0c070';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#d4a574';
                  e.currentTarget.style.color = '#d4a574';
                }}
              >
                {data.reject_button_text}
              </button>
              <button
                onClick={handleAccept}
                className="prompt-medium-font"
                style={{
                  color: '#3a1a0a',
                  background: 'linear-gradient(135deg, #f0c070 0%, #e0a850 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.55vw 2vw',
                  fontSize: '0.78vw',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 8px rgba(240, 192, 112, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #f5d090 0%, #e8b860 100%)';
                  e.currentTarget.style.transform = 'scale(1.03)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #f0c070 0%, #e0a850 100%)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                {data.accept_button_text}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div
        className="cookie-consent-mobile fixed bottom-0 left-0 right-0 z-[9998]"
        style={{
          animation: 'slideUp 0.5s ease-out forwards'
        }}
      >
        <div
          style={{
            background: 'linear-gradient(135deg, #3a1a0a 0%, #562310 50%, #6B3410 100%)',
            borderTop: '3px solid #8B4513',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
            padding: '5vw 5vw 6vw 5vw'
          }}
        >
          <div className="flex items-start" style={{ gap: '3vw', marginBottom: '4vw' }}>
            <span style={{ fontSize: '7vw', flexShrink: 0, lineHeight: 1 }}>🍪</span>
            <p
              className="prompt-regular-font"
              style={{
                color: '#ffffff',
                fontSize: '3.4vw',
                lineHeight: '1.5',
                margin: 0
              }}
            >
              {renderText(data.mobile_text)}
            </p>
          </div>

          <div className="flex items-center" style={{ gap: '3vw' }}>
            <button
              onClick={handleReject}
              className="prompt-medium-font"
              style={{
                flex: 1,
                color: '#d4a574',
                background: 'transparent',
                border: '1.5px solid #d4a574',
                borderRadius: '8px',
                padding: '2.5vw 0',
                fontSize: '3.4vw',
                cursor: 'pointer'
              }}
            >
              {data.reject_button_text}
            </button>
            <button
              onClick={handleAccept}
              className="prompt-medium-font"
              style={{
                flex: 1,
                color: '#3a1a0a',
                background: 'linear-gradient(135deg, #f0c070 0%, #e0a850 100%)',
                border: 'none',
                borderRadius: '8px',
                padding: '2.5vw 0',
                fontSize: '3.4vw',
                cursor: 'pointer',
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(240, 192, 112, 0.3)'
              }}
            >
              {data.accept_button_text}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
